/**
 * `useMaximoRecords()` — fetch a named set of per-machine Maximo records.
 *
 * `shared/useOemData.ts` ships `loadMachineRecord(id)`, which the machine page
 * calls once. Two figures on the agents page — the service-promise tally and
 * the upgrade-candidate count — are derived over a *set* of machines, so this
 * hook resolves the set in one `Promise.all` and hands back the map that
 * `MaximoCtx` wants.
 *
 * The set is deliberately small: `AgentsScene.transform.ts`'s
 * `maximoMachinesNeeded()` returns only the machines that can change either
 * figure (about thirty of the hundred), and the arithmetic is identical to
 * walking all of them.
 *
 * A record that fails to load is simply absent from the map. `mxOf()`
 * normalises a missing entry to `null` exactly as the demo's `B.mx[id] || null`
 * did, so a partial map degrades the two figures rather than breaking the page.
 *
 * UI layer support; the cache and the fetch live in `shared/useOemData.ts`.
 *
 * @see src/versions/v1/modules/integration/AgentsScene.transform.ts
 * @see src/versions/v1/shared/useOemData.ts — `loadMachineRecord`
 */

import { useEffect, useState } from 'react';
import type { MaximoAsset } from '../../schema/service';
import { loadMachineRecord } from '../../shared/useOemData';

/** What {@link useMaximoRecords} returns. */
export interface MaximoRecordsState {
  /** Machine id → the Meridian service-register record. */
  records: Readonly<Record<string, MaximoAsset | undefined>>;
  /** `true` until every request has settled, successfully or not. */
  loading: boolean;
}

/**
 * Load the Meridian Maximo asset record for each id.
 *
 * `machineIds` is joined into the effect key rather than used directly, so a
 * caller can pass a freshly built array on every render without re-fetching.
 */
export function useMaximoRecords(machineIds: readonly string[]): MaximoRecordsState {
  const key = machineIds.join(',');
  const [state, setState] = useState<MaximoRecordsState>({ records: {}, loading: true });

  useEffect(() => {
    let live = true;
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setState({ records: {}, loading: false });
      return;
    }
    setState({ records: {}, loading: true });

    Promise.all(
      ids.map((id) =>
        loadMachineRecord(id).then(
          (record) => [id, record.mx] as const,
          () => [id, undefined] as const,
        ),
      ),
    ).then((pairs) => {
      if (!live) return;
      const records: Record<string, MaximoAsset | undefined> = {};
      for (const [id, asset] of pairs) if (asset) records[id] = asset;
      setState({ records, loading: false });
    });

    return () => {
      live = false;
    };
  }, [key]);

  return state;
}
